import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  PlanMatriz,
  PlanMatrizAlias,
  PlanMatrizList,
  UpdatePlanMatriz,
  CreatePlanMatrizAlias,
  EstadoOt,
} from '@sime/shared';

// --- Response Types ---

interface PlanMatrizListItem extends PlanMatriz {
  ambito?: { id: number; nombre: string };
  subcontrato?: { id: number; nombre: string };
  _count?: { aliases: number; ots: number };
}

interface PaginatedPlanMatrizResponse {
  success: true;
  data: PlanMatrizListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface PlanMatrizSearchResult {
  id: number;
  nombre: string;
  codigo: string | null;
  similarity: number;
}

interface PlanMatrizSearchResponse {
  success: true;
  data: PlanMatrizSearchResult[];
}

// --- Dashboard Types ---

export interface MonthDashboardData {
  month: number;
  year: number;
  avancePercent: number;
  totalHH: number;
  pendientes: number;
  cerrados: number;
  noEjecutados: number;
  enEjecucion: number;
  validados: number;
  totalTickets: number;
}

interface DashboardResponse {
  success: true;
  data: MonthDashboardData[];
}

// --- Query Keys ---

export const planMatrizKeys = {
  all: ['plan-matriz'] as const,
  lists: () => [...planMatrizKeys.all, 'list'] as const,
  list: (params: Partial<PlanMatrizList>) =>
    [...planMatrizKeys.lists(), params] as const,
  searches: () => [...planMatrizKeys.all, 'search'] as const,
  search: (q: string, ambitoId?: number) =>
    [...planMatrizKeys.searches(), q, ambitoId] as const,
  details: () => [...planMatrizKeys.all, 'detail'] as const,
  detail: (id: number) => [...planMatrizKeys.details(), id] as const,
  dashboard: (year: number, ambitoId?: number) =>
    [...planMatrizKeys.all, 'dashboard', year, ambitoId] as const,
};

// --- Query Hooks ---

export function usePlanMatrizList(
  params: Partial<PlanMatrizList> = {},
  options?: Omit<UseQueryOptions<PaginatedPlanMatrizResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.ambitoId) searchParams.set('ambitoId', String(params.ambitoId));
  if (params.subcontratoId) searchParams.set('subcontratoId', String(params.subcontratoId));
  if (params.activo !== undefined) searchParams.set('activo', String(params.activo));
  if (params.search) searchParams.set('search', params.search);

  const qs = searchParams.toString();
  const endpoint = `/plan-matriz${qs ? `?${qs}` : ''}`;

  return useQuery({
    queryKey: planMatrizKeys.list(params),
    queryFn: () => apiClient<PaginatedPlanMatrizResponse>(endpoint),
    ...options,
  });
}

export function usePlanMatrizSearch(
  q: string,
  ambitoId?: number,
  options?: Omit<UseQueryOptions<PlanMatrizSearchResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  searchParams.set('q', q);
  if (ambitoId) searchParams.set('ambitoId', String(ambitoId));

  return useQuery({
    queryKey: planMatrizKeys.search(q, ambitoId),
    queryFn: () => apiClient<PlanMatrizSearchResponse>(`/plan-matriz/search?${searchParams.toString()}`),
    enabled: q.length >= 2,
    ...options,
  });
}

// --- Dashboard Query Hook ---

export function usePlanMatrizDashboard(
  year: number,
  ambitoId?: number,
  options?: Omit<UseQueryOptions<DashboardResponse>, 'queryKey' | 'queryFn'>,
) {
  const searchParams = new URLSearchParams();
  searchParams.set('year', String(year));
  if (ambitoId !== undefined && ambitoId > 0) {
    searchParams.set('ambitoId', String(ambitoId));
  }

  return useQuery({
    queryKey: planMatrizKeys.dashboard(year, ambitoId),
    queryFn: () => apiClient<DashboardResponse>(`/plan-matriz/dashboard/annual?${searchParams.toString()}`),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// --- Detail Types ---

interface PlanMatrizDetail extends PlanMatriz {
  ambito?: { id: number; nombre: string };
  subcontrato?: { id: number; nombre: string };
  ubicacion?: { id: number; nombre: string };
  empresa?: { id: number; nombre: string };
  tipoMantenimiento?: { id: number; nombre: string };
  pauta?: { id: number; nombre: string; codigo: string } | null;
  turno?: { id: number; nombre: string } | null;
  catalogoeco?: { id: number; item: string; eco: string; unidad: string | null } | null;
  tipoEquipo?: { id: number; detalle: string } | null;
  aliases: PlanMatrizAlias[];
}

interface SinglePlanMatrizResponse {
  success: true;
  data: PlanMatrizDetail;
}

interface PlanMatrizAliasResponse {
  success: true;
  data: PlanMatrizAlias;
}

interface PlanMatrizTicketItem {
  id: number;
  correlativo: string;
  estadoOt: EstadoOt;
  periodo: string;
  createdAt: string;
}

interface PlanMatrizTicketsResponse {
  success: true;
  data: PlanMatrizTicketItem[];
}

interface MessageResponse {
  success: true;
  data: { message: string };
}

// --- Detail Query Hook ---

export function usePlanMatriz(
  id: number,
  options?: Omit<UseQueryOptions<SinglePlanMatrizResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: planMatrizKeys.detail(id),
    queryFn: () => apiClient<SinglePlanMatrizResponse>(`/plan-matriz/${id}`),
    enabled: id > 0,
    ...options,
  });
}

// --- Related Tickets Query Hook ---

export function usePlanMatrizTickets(
  id: number,
  options?: Omit<UseQueryOptions<PlanMatrizTicketsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...planMatrizKeys.detail(id), 'tickets'] as const,
    queryFn: () => apiClient<PlanMatrizTicketsResponse>(`/plan-matriz/${id}/tickets`),
    enabled: id > 0,
    ...options,
  });
}

// --- Mutation Hooks ---

export function useUpdatePlanMatriz(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePlanMatriz) =>
      apiClient<SinglePlanMatrizResponse>(`/plan-matriz/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.lists() });
    },
  });
}

export function useCreatePlanMatrizAlias(planMatrizId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlanMatrizAlias) =>
      apiClient<PlanMatrizAliasResponse>(`/plan-matriz/${planMatrizId}/aliases`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.detail(planMatrizId) });
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.lists() });
    },
  });
}

export function useDeletePlanMatrizAlias(planMatrizId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: number) =>
      apiClient<MessageResponse>(`/plan-matriz/${planMatrizId}/aliases/${aliasId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.detail(planMatrizId) });
      void queryClient.invalidateQueries({ queryKey: planMatrizKeys.lists() });
    },
  });
}

// Re-export types for page components
export type { PlanMatrizListItem, PlanMatrizDetail, PlanMatrizSearchResult, PlanMatrizTicketItem };
